import React, { useEffect, useRef, useState } from 'react';
import { Upload, Camera, Video, Images, X, Film, Box as BoxIcon, Check, AlertTriangle, FileX } from 'lucide-react';
import { api } from '../lib/api';
import { ActionBtn } from '../components/ReorderControls';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fieldCls = 'mt-1 w-full bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-3 py-2.5 rounded';
const OK_EXT = /\.(mp4|mov|webm|m4v|jpe?g|png|webp|gif|glb|gltf)$/i;
const ACCEPT = 'image/*,video/*,.mp4,.mov,.webm,.m4v,.jpg,.jpeg,.png,.webp,.gif,.glb,.gltf';

const kindOf = (f) => (/\.(glb|gltf)$/i.test(f.name) ? 'model' : f.type.startsWith('video') || /\.(mp4|mov|webm|m4v)$/i.test(f.name) ? 'video' : 'image');
const titleFrom = (name) => name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim() || 'Untitled';
const sizeLabel = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

let uidSeq = 0;

const toEntry = (file) => {
  const supported = OK_EXT.test(file.name);
  const kind = supported ? kindOf(file) : 'unsupported';
  return {
    uid: ++uidSeq, file, kind, title: titleFrom(file.name), progress: 0,
    status: supported ? 'ready' : 'error',
    error: supported ? null : 'Unsupported file type',
    preview: supported && kind !== 'model' ? URL.createObjectURL(file) : null
  };
};

export default function UploadForm({ categories, onUploaded, className = '' }) {
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [queue, setQueue] = useState([]);
  const [poster, setPoster] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const refs = { roll: useRef(null), photo: useRef(null), video: useRef(null) };
  const queueRef = useRef(queue);
  queueRef.current = queue;

  useEffect(() => {
    if (categories.length && !categories.some((c) => c.key === category)) setCategory(categories[0].key);
  }, [categories, category]);
  useEffect(() => () => queueRef.current.forEach((q) => q.preview && URL.revokeObjectURL(q.preview)), []);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) { setQueue((q) => [...q, ...files.map(toEntry)]); setMsg(null); }
  };
  const pick = (e) => { addFiles(e.target.files); e.target.value = ''; };
  const patch = (uid, p) => setQueue((q) => q.map((x) => (x.uid === uid ? { ...x, ...p } : x)));
  const removeEntry = (entry) => {
    if (entry.preview) URL.revokeObjectURL(entry.preview);
    setQueue((q) => q.filter((x) => x.uid !== entry.uid));
  };

  const single = queue.length === 1 ? queue[0] : null;
  const pending = queue.filter((q) => q.status !== 'done' && OK_EXT.test(q.file.name));

  const uploadOne = async (entry) => {
    const fd = new FormData();
    fd.append('file', entry.file);
    if (single?.kind === 'video' && poster) fd.append('poster', poster);
    fd.append('category', category);
    fd.append('title', entry.title.trim() || titleFrom(entry.file.name));
    fd.append('date', date);
    fd.append('description', description);
    patch(entry.uid, { status: 'uploading', error: null, progress: 0 });
    try {
      await api.post('/admin/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => evt.total && patch(entry.uid, { progress: Math.round((evt.loaded / evt.total) * 100) })
      });
      patch(entry.uid, { status: 'done', progress: 100 });
      return true;
    } catch (err) {
      const tooBig = err?.response?.status === 413;
      patch(entry.uid, { status: 'error', error: tooBig ? 'File too large' : err?.response?.data?.detail || 'Upload failed' });
      return false;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pending.length) { setMsg({ type: 'err', text: 'Choose at least one photo, video or model first.' }); return; }
    setBusy(true);
    setMsg(null);
    let ok = 0;
    // Sequential, last file first: each upload lands on top, so the channel ends up in queue order.
    for (const entry of [...pending].reverse()) {
      if (await uploadOne(entry)) ok += 1;
    }
    setBusy(false);
    const failed = pending.length - ok;
    setQueue((q) => q.filter((x) => {
      if (x.status === 'done' && x.preview) URL.revokeObjectURL(x.preview);
      return x.status !== 'done';
    }));
    if (ok) { setPoster(null); onUploaded(category); }
    setMsg(failed
      ? { type: 'err', text: `${ok} uploaded, ${failed} failed — fix or remove them and try again.` }
      : { type: 'ok', text: `${ok} broadcast${ok === 1 ? '' : 's'} uploaded.` });
  };

  return (
    <form onSubmit={submit} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          className={`border-2 border-[#3a2512] bg-[#0f0704] p-4 sm:p-6 rounded-lg space-y-4 ${className}`} data-testid="admin-upload-form">
      <div className="hidden lg:block font-osd text-[#f5a623] text-sm tracking-widest">// NEW BROADCAST</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="font-osd text-xs text-[#a88552] tracking-widest">CHANNEL</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={fieldCls} data-testid="admin-field-category">
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="font-osd text-xs text-[#a88552] tracking-widest">DATE</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={fieldCls} data-testid="admin-field-date" required />
        </label>
      </div>

      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest">DESCRIPTION <span className="opacity-60">(optional · applies to every file)</span></span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Short synopsis…"
                  className={`${fieldCls} resize-y`} data-testid="admin-field-description" />
      </label>

      <Pickers refs={refs} onPick={pick} />

      {queue.length > 0 && (
        <ul className="space-y-2" aria-label="Files to upload" data-testid="admin-upload-queue">
          {queue.map((entry, i) => (
            <QueueRow key={entry.uid} entry={entry} index={i} busy={busy}
                      onTitle={(t) => patch(entry.uid, { title: t })} onRemove={() => removeEntry(entry)} />
          ))}
        </ul>
      )}

      {single?.kind === 'video' && (
        <label className="block">
          <span className="font-osd text-xs text-[#a88552] tracking-widest">POSTER IMAGE (optional — cover for the video)</span>
          <input type="file" accept="image/*" onChange={(e) => setPoster(e.target.files?.[0] || null)}
                 className="mt-1 w-full font-osd text-xs text-[#d8b887] file:mr-3 file:py-2.5 file:px-3 file:border file:border-[#5a3418] file:bg-[#1a0e05] file:text-[#f5a623] file:font-osd file:text-xs file:cursor-pointer file:rounded"
                 data-testid="admin-field-poster" />
        </label>
      )}

      {msg && (
        <div role="status" data-testid="admin-upload-status"
             className={`font-osd text-xs px-3 py-2 rounded border ${msg.type === 'ok' ? 'text-[#9dd68a] border-[#9dd68a]/50 bg-[#0e1a09]' : 'text-[#ff6a3a] border-[#ff6a3a]/50 bg-[#1a0e05]'}`}>
          {msg.text}
        </div>
      )}

      <button type="submit" disabled={busy || !pending.length} data-testid="admin-upload-submit"
              className="admin-action w-full h-12 flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-5 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <Upload className="w-4 h-4" />
        {busy ? 'TRANSMITTING…' : pending.length > 1 ? `BROADCAST ${pending.length} FILES` : 'BROADCAST UPLOAD'}
      </button>
    </form>
  );
}

function Pickers({ refs, onPick }) {
  const hidden = 'sr-only';
  return (
    <div className="space-y-2">
      <span className="font-osd text-xs text-[#a88552] tracking-widest">FILES <span className="opacity-60">(photos, videos or .glb / .gltf models)</span></span>
      <div className="touch-only grid grid-cols-2 gap-2">
        <PickerBtn onClick={() => refs.photo.current.click()} icon={Camera} testId="admin-pick-camera-photo">TAKE PHOTO</PickerBtn>
        <PickerBtn onClick={() => refs.video.current.click()} icon={Video} testId="admin-pick-camera-video">RECORD VIDEO</PickerBtn>
      </div>
      <PickerBtn onClick={() => refs.roll.current.click()} icon={Images} testId="admin-pick-files" primary>
        <span className="touch-only">CAMERA ROLL / FILES</span>
        <span className="mouse-only">CHOOSE FILES <span className="opacity-60 hidden sm:inline">· OR DROP THEM HERE</span></span>
      </PickerBtn>
      <input ref={refs.roll} type="file" multiple accept={ACCEPT} onChange={onPick} className={hidden} tabIndex={-1} aria-hidden data-testid="admin-field-file" />
      <input ref={refs.photo} type="file" accept="image/*" capture="environment" onChange={onPick} className={hidden} tabIndex={-1} aria-hidden data-testid="admin-field-camera-photo" />
      <input ref={refs.video} type="file" accept="video/*" capture="environment" onChange={onPick} className={hidden} tabIndex={-1} aria-hidden data-testid="admin-field-camera-video" />
    </div>
  );
}

function PickerBtn({ children, onClick, icon: Icon, testId, primary }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`admin-action w-full h-12 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg font-osd text-xs tracking-widest transition-colors ${primary ? 'border-[#f5a623] text-[#ffc968] bg-[#1a0e05] hover:bg-[#2a1608]' : 'border-[#5a3418] text-[#d8b887] bg-[#1a0e05] hover:border-[#f5a623] hover:text-[#ffc968]'}`}>
      <Icon className="w-4 h-4 shrink-0" /> {children}
    </button>
  );
}

function QueueRow({ entry, index, busy, onTitle, onRemove }) {
  const { kind, preview, status, progress, error, file } = entry;
  return (
    <li className={`flex gap-3 items-center p-2 border rounded-lg bg-[#1a0e05]/60 ${status === 'error' ? 'border-[#7a2a14]' : 'border-[#3a2512]'}`} data-testid={`admin-queue-item-${index}`}>
      <div className="w-14 h-14 shrink-0 rounded overflow-hidden bg-[#0a0502] border border-[#3a2512] flex items-center justify-center relative">
        {kind === 'image' && preview && <img src={preview} alt="" className="w-full h-full object-cover" />}
        {kind === 'video' && preview && <video src={preview} muted playsInline preload="metadata" className="w-full h-full object-cover" />}
        {kind === 'model' && <BoxIcon className="w-6 h-6 text-[#f5a623]" />}
        {kind === 'unsupported' && <FileX className="w-6 h-6 text-[#ff6a3a]" />}
        {kind === 'video' && <Film className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 text-[#ffc968] drop-shadow" />}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <input value={entry.title} onChange={(e) => onTitle(e.target.value)} disabled={busy || status === 'done'} aria-label={`Title for ${file.name}`}
               className="w-full bg-[#0f0704] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-2 py-1.5 rounded disabled:opacity-60"
               data-testid={`admin-queue-title-${index}`} />
        <div className="font-osd text-[10px] tracking-widest flex items-center gap-2">
          <span className="text-[#8b6a3e] uppercase">{kind} · {sizeLabel(file.size)}</span>
          {status === 'done' && <span className="text-[#9dd68a] flex items-center gap-1"><Check className="w-3 h-3" /> SENT</span>}
          {status === 'error' && <span className="text-[#ff6a3a] flex items-center gap-1 truncate" role="alert"><AlertTriangle className="w-3 h-3 shrink-0" /> {error}</span>}
        </div>
        {status === 'uploading' && (
          <div className="h-1.5 bg-[#2a1608] rounded overflow-hidden" data-testid="admin-upload-progress">
            <div className="h-full bg-[#f5a623] transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      <ActionBtn onClick={onRemove} disabled={busy} label={`Remove ${file.name}`} testId={`admin-queue-remove-${index}`} tone="danger">
        <X className="w-4 h-4" />
      </ActionBtn>
    </li>
  );
}
